/**
 * Compliance documents — current versions.
 * Bumping ANY of these forces all users to re-accept on next visit.
 */
export const CURRENT_RISK_DISCLOSURE_VERSION = "v1.0";
export const CURRENT_DISCLAIMER_VERSION = "v1.0";
export const CURRENT_TERMS_VERSION = "v1.0";

/**
 * Canonical Telegram / outbound message disclaimer.
 *
 * MUST appear at the end of every outbound signal — server-side and client-side
 * paths alike. Includes all four mandatory clauses required by MiFID II and
 * the audit guidance:
 *   1. Educational intelligence only
 *   2. Not financial advice
 *   3. Past performance does not guarantee future results
 *   4. Forex/CFD trading involves risk
 *
 * Do NOT shorten or paraphrase. A CI test asserts every outbound message
 * contains this exact string.
 */
export const CANONICAL_TELEGRAM_DISCLAIMER = [
  "",
  "━━━━━━━━━━━━━━━━━━━━",
  "⚠️ _Educational intelligence only_",
  "_Not financial advice_",
  "_Past performance does not guarantee future results_",
  "_Forex/CFD trading involves risk_",
  "_Trade responsibly_ | forexmind.ai",
  "━━━━━━━━━━━━━━━━━━━━",
].join("\n");

export const DEFAULT_RESTRICTED_JURISDICTIONS = ["US", "CA", "BE", "NZ"] as const;
export type JurisdictionPolicyMode = "warn" | "block";

export interface JurisdictionsConfig {
  mode: JurisdictionPolicyMode;
  warning: string[];
  blocked: string[];
}

export const DEFAULT_JURISDICTIONS_CONFIG: JurisdictionsConfig = {
  mode: "warn",
  warning: [...DEFAULT_RESTRICTED_JURISDICTIONS],
  blocked: [],
};

export interface ComplianceVersions {
  risk_disclosure: string;
  disclaimer: string;
  terms: string;
  updated_at: string | null;
}

export const DEFAULT_VERSIONS: ComplianceVersions = {
  risk_disclosure: CURRENT_RISK_DISCLOSURE_VERSION,
  disclaimer: CURRENT_DISCLAIMER_VERSION,
  terms: CURRENT_TERMS_VERSION,
  updated_at: null,
};

export function classifyCountry(
  code: string | null | undefined,
  cfg: JurisdictionsConfig,
): "ok" | "warning" | "blocked" {
  if (!code) return "ok";
  const up = code.toUpperCase();
  if (cfg.blocked.includes(up)) return "blocked";
  if (cfg.warning.includes(up)) return "warning";
  return "ok";
}

/** ISO 3166-1 alpha-2 — common-enough subset for the country dropdown. */
export const COUNTRY_LIST: ReadonlyArray<{ code: string; name: string }> = [
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
  { code: "GB", name: "United Kingdom" },
  { code: "IE", name: "Ireland" },
  { code: "AU", name: "Australia" },
  { code: "NZ", name: "New Zealand" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "IT", name: "Italy" },
  { code: "ES", name: "Spain" },
  { code: "PT", name: "Portugal" },
  { code: "NL", name: "Netherlands" },
  { code: "BE", name: "Belgium" },
  { code: "LU", name: "Luxembourg" },
  { code: "CH", name: "Switzerland" },
  { code: "AT", name: "Austria" },
  { code: "SE", name: "Sweden" },
  { code: "NO", name: "Norway" },
  { code: "FI", name: "Finland" },
  { code: "DK", name: "Denmark" },
  { code: "PL", name: "Poland" },
  { code: "CZ", name: "Czechia" },
  { code: "GR", name: "Greece" },
  { code: "RO", name: "Romania" },
  { code: "BG", name: "Bulgaria" },
  { code: "HU", name: "Hungary" },
  { code: "TR", name: "Turkey" },
  { code: "IL", name: "Israel" },
  { code: "AE", name: "United Arab Emirates" },
  { code: "SA", name: "Saudi Arabia" },
  { code: "ZA", name: "South Africa" },
  { code: "NG", name: "Nigeria" },
  { code: "EG", name: "Egypt" },
  { code: "KE", name: "Kenya" },
  { code: "IN", name: "India" },
  { code: "PK", name: "Pakistan" },
  { code: "BD", name: "Bangladesh" },
  { code: "SG", name: "Singapore" },
  { code: "MY", name: "Malaysia" },
  { code: "ID", name: "Indonesia" },
  { code: "PH", name: "Philippines" },
  { code: "TH", name: "Thailand" },
  { code: "VN", name: "Vietnam" },
  { code: "JP", name: "Japan" },
  { code: "KR", name: "South Korea" },
  { code: "HK", name: "Hong Kong" },
  { code: "TW", name: "Taiwan" },
  { code: "CN", name: "China" },
  { code: "BR", name: "Brazil" },
  { code: "AR", name: "Argentina" },
  { code: "CL", name: "Chile" },
  { code: "MX", name: "Mexico" },
  { code: "CO", name: "Colombia" },
  { code: "PE", name: "Peru" },
  { code: "OT", name: "Other / not listed" },
];
